/**
 * useShareBasemapPersistence
 *
 * Owns the public-share viewer's basemap persistence — the only place that
 * knows both the in-memory store key (`share:<token>`) and the localStorage key
 * (`share-basemap:<token>`). Public viewers have no server-side user settings,
 * so localStorage is the sole durable medium (mirrors how the authenticated map
 * uses `settingsBridge` server-side).
 *
 * On mount it seeds the store pick from an explicit embed `?basemap=` (highest
 * priority) or the last persisted pick; thereafter every change is written back.
 *
 * @module components/share/public/PublicMapShell/hooks/useShareBasemapPersistence
 */
import { useEffect, useMemo, useRef } from 'react';

import { isValidBasemapId } from '@/config/maplibre';
import { useGeoMarkupStore } from '@/store';
import { localStorage as storage } from '@/utils/helpers';

import {
  buildShareBasemapKey,
  buildShareBasemapPersistKey,
} from '../../usePublicShareHydration';

export function useShareBasemapPersistence(
  token: string,
  allowedBasemaps: string[],
  initialBasemapId?: string
): void {
  const shareKey = useMemo(() => buildShareBasemapKey(token), [token]);
  const setUserBasemapForWorkspace = useGeoMarkupStore(
    (state) => state.map.setUserBasemapForWorkspace
  );
  const viewerBasemap = useGeoMarkupStore(
    (state) => state.map.userBasemapByWorkspace[shareKey]
  );

  // Read the current allow-list without re-seeding on its array identity — the
  // seed must run once per share, not on every parent re-render.
  const allowedBasemapsRef = useRef(allowedBasemaps);
  allowedBasemapsRef.current = allowedBasemaps;

  // Seed the starting pick: an explicit embed `?basemap=` wins, otherwise restore
  // the last pick this browser persisted. With neither, the public basemap
  // context's default applies (selectResolvedBasemapId). Clamped to the allow-list.
  useEffect(() => {
    const explicit =
      initialBasemapId && isValidBasemapId(initialBasemapId)
        ? initialBasemapId
        : null;
    const persisted = storage.get<string | null>(
      buildShareBasemapPersistKey(token),
      null
    );
    const restored =
      persisted &&
      isValidBasemapId(persisted) &&
      allowedBasemapsRef.current.includes(persisted)
        ? persisted
        : null;
    const seed = explicit ?? restored;
    if (seed) {
      setUserBasemapForWorkspace(shareKey, seed);
    }
  }, [initialBasemapId, token, shareKey, setUserBasemapForWorkspace]);

  // Persist the viewer's pick so a reload restores it.
  useEffect(() => {
    if (viewerBasemap) {
      storage.set(buildShareBasemapPersistKey(token), viewerBasemap);
    }
  }, [viewerBasemap, token]);
}
